<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <!-- Brand logo-->
      <b-link class="brand-logo">
        <mom-logo />
        <h2 class="brand-text text-primary ml-1">
          {{ $t('general.mom') }}
        </h2>
      </b-link>
      <b-col
        lg="8"
        class="d-none d-lg-flex align-items-center p-5"
        style="background-color: #ececec"
      >
        <div class="w-100 d-lg-flex align-items-center justify-content-center px-5">
          <b-img
            fluid
            :src="imgUrl"
            alt="Login"
          />
        </div>
      </b-col>
      <b-col
        lg="4"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto"
        >
          <p
            v-if="$route.query.logout === 'revocation-unconfirmed'"
            role="alert"
          >
            {{ logoutWarning }}
          </p>
          <b-form
            class="auth-login-form mt-2"
            @submit.prevent="login"
          >
            <b-form-group
              :label="$t('login.phoneNumber')"
              label-for="login-phoneNumber"
            >
              <b-form-input
                id="login-phoneNumber"
                v-model="phoneNumber"
                :state="errorMessage !== '' ? false:null"
                name="login-phoneNumber"
                placeholder="9128458991"
              />
            </b-form-group>
            <b-form-group>
              <div class="d-flex justify-content-between">
                <label for="login-password">{{ $t('login.password') }}</label>
              </div>
              <validation-provider
                name="Password"
                vid="password"
                rules="required"
              >
                <b-input-group
                  class="input-group-merge"
                  :class="errorMessage !== '' ? 'is-invalid':null"
                >
                  <b-form-input
                    id="login-password"
                    v-model="password"
                    :state="errorMessage !== '' ? false:null"
                    class="form-control-merge"
                    :type="passwordFieldType"
                    name="login-password"
                    placeholder="Password"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      class="cursor-pointer"
                      :icon="passwordToggleIcon"
                      @click="togglePasswordVisibility"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errorMessage }}</small>
              </validation-provider>
            </b-form-group>
            <b-button
              type="submit"
              variant="primary"
              block
            >
              {{ $t('login.login') }}
            </b-button>
          </b-form>
        </b-col>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import loginImage from '@/assets/images/pages/login.jpg'
import darkLoginImage from '@/assets/images/pages/login-v2-dark.svg'

import {
  BButton, BCol, BForm, BFormGroup, BFormInput, BImg, BInputGroup, BInputGroupAppend, BLink, BRow, VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import MomLogo from '@core/layouts/components/Logo.vue'
import useJwt from '@/auth/jwt/useJwt'
import { required } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import store from '@/store/index'
import { getHomeRouteForLoggedInUser } from '@/auth/utils'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BImg,
    BForm,
    BButton,
    MomLogo,
    ValidationProvider,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      status: '',
      password: '',
      phoneNumber: '',
      sideImg: loginImage,
      required,
      errorMessage: '',
    }
  },
  computed: {
    logoutWarning() {
      const messages = { fa: 'از این مرورگر خارج شدید، اما لغو نشست در سرور تأیید نشد. اگر نگران دسترسی هستید با مسئول سامانه تماس بگیرید.', en: 'You signed out of this browser, but server session revocation could not be confirmed. Contact your administrator if you are concerned about access.', ar: 'تم تسجيل الخروج من هذا المتصفح، لكن تعذّر تأكيد إلغاء الجلسة على الخادم. تواصل مع مسؤول النظام إذا كنت قلقًا بشأن الوصول.' }
      return messages[this.$i18n.locale] || messages.fa
    },
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeOffIcon' : 'EyeIcon'
    },
    imgUrl() {
      return store.state.appConfig.layout.skin === 'dark' ? darkLoginImage : this.sideImg
    },
  },
  methods: {
    defineAbilityFromPermissions(permissions) {
      const abilities = [{
        subject: 'dashboard',
        action: 'read',
      }, {
        action: 'read',
        subject: 'Auth',
      }]
      permissions.forEach(permission => {
        const permissionSplit = permission.split('/')
        if (permissionSplit.length === 2) {
          abilities.push({
            subject: permissionSplit[0],
            action: permissionSplit[1],
          })
        } else if (permission === 'manageAll') {
          abilities.push({
            subject: 'all',
            action: 'manage',
          })
        }
      })
      return abilities
    },
    createPermissions(userRoles) {
      return userRoles.map(item => item.permissions).reduce((a, b) => a.concat(b))
    },
    login() {
      useJwt.login({
        phoneNumber: this.phoneNumber,
        password: this.password,
      }).then(response => {
        const userData = response.data
        userData.user.permissions = this.createPermissions(userData.user.permissions)
        userData.user.ability = this.defineAbilityFromPermissions(userData.user.permissions)
        useJwt.setToken(`Bearer ${response.data.access_token}`)
        localStorage.setItem('userData', JSON.stringify(userData.user))
        this.$ability.update(userData.user.ability)
        this.$router.replace(getHomeRouteForLoggedInUser(userData.user.roles)).then(() => {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('login.welcomeMessage', [userData.user.fullName || userData.user.username]),
              icon: 'CoffeeIcon',
              variant: 'success',
              text: this.$t('login.userDetailMessage', [userData.user.phoneNumber]),
            },
          })
        })
      }).catch(error => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.response.data.message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        this.errorMessage = error.response.data.message
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>
